import React, { useState, useEffect } from 'react';
import { Users, Plus, Search, Edit, Trash2, Phone, Mail, Calendar, MapPin, FileText } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import CustomerForm from './CustomerForm';
import { GoogleMapsLoader } from '../../components/maps/GoogleMapsLoader';
import apiService from '../../services/apiService';
import { formatDate } from '../../utils/formatters';
import './CustomerManagementPage.css';

const CustomerManagementPage = () => {
    const { loading, setLoading, setError, clearError, showToast } = useApp();

    const [clientes, setClientes] = useState([]);
    const [filteredClientes, setFilteredClientes] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedCliente, setSelectedCliente] = useState(null);

    useEffect(() => {
        loadClientes();
    }, []);

    useEffect(() => {
        filterClientes();
    }, [clientes, searchQuery]);

    const loadClientes = async () => {
        try {
            setLoading(true);
            clearError();

            const result = await apiService.getClientes();

            if (result.success) {
                setClientes(result.data);
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al cargar clientes');
        } finally {
            setLoading(false);
        }
    };

    const filterClientes = () => {
        let filtered = clientes;

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(cliente => {
                // Buscar en campos básicos
                const matchesBasic = cliente.nombre?.toLowerCase().includes(query) ||
                    cliente.apellido?.toLowerCase().includes(query) ||
                    cliente.nombre_comercial?.toLowerCase().includes(query);

                // Buscar en teléfonos
                const matchesTelefono = cliente.telefonos?.some(tel =>
                    tel.telefono.includes(query)
                );

                // Buscar en direcciones
                const matchesDireccion = cliente.direcciones?.some(dir =>
                    dir.direccion.toLowerCase().includes(query)
                );

                return matchesBasic || matchesTelefono || matchesDireccion;
            });
        }

        setFilteredClientes(filtered);
    };

    const handleCreateCliente = async (clienteData) => {
        try {
            const result = await apiService.crearCliente(clienteData);

            if (result.success) {
                setShowCreateModal(false);
                showToast('Cliente creado exitosamente', 'success');
                loadClientes();
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al crear cliente', 'error');
        }
    };

    const handleEditCliente = async (clienteData) => {
        try {
            const result = await apiService.actualizarCliente({
                id: selectedCliente.id,
                ...clienteData
            });

            if (result.success) {
                showToast('Cliente actualizado exitosamente', 'success');
                loadClientes();
                // Actualizar el cliente seleccionado con los nuevos datos
                setSelectedCliente({
                    ...selectedCliente,
                    ...clienteData
                });
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al actualizar cliente', 'error');
        }
    };

    const handleDeleteCliente = async (id, nombre, apellido) => {
        const nombreCompleto = `${nombre} ${apellido}`;
        if (!window.confirm(`¿Estás seguro de que quieres desactivar al cliente "${nombreCompleto}"?\n\nNota: El cliente se ocultará de la lista pero se mantendrá en el historial.`)) {
            return;
        }

        try {
            const result = await apiService.eliminarCliente(id);

            if (result.success) {
                showToast('Cliente desactivado exitosamente', 'success');
                loadClientes();
            } else {
                showToast(result.message, 'error');
            }
        } catch (error) {
            showToast('Error al desactivar cliente', 'error');
        }
    };


    return (
        <GoogleMapsLoader>
            <div className="customer-management-page">

                {/* Header */}


                {/* Controles */}
                <div className="customer-controls">
                    <div className="search-filters">
                        <div className="search-container">
                            <Input
                                placeholder="Buscar por nombre, nombre comercial, teléfono o dirección..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                icon={<Search size={20} />}
                                className="search-input"
                            />
                        </div>
                    </div>

                    <div className="controls-actions">
                        <Button
                            onClick={() => setShowCreateModal(true)}
                            disabled={loading}
                        >

                            Nuevo Cliente
                        </Button>
                    </div>
                </div>

                {/* Tabla de clientes */}
                <div className="customers-table-container">
                    {loading && (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Cargando clientes...</p>
                        </div>
                    )}

                    {!loading && filteredClientes.length === 0 && (
                        <div className="empty-state">
                            <Users size={48} />
                            <h3>No se encontraron clientes</h3>
                            <p>
                                {searchQuery
                                    ? 'No hay clientes que coincidan con los filtros'
                                    : 'No hay clientes registrados'
                                }
                            </p>
                            {!searchQuery && (
                                <Button
                                    onClick={() => setShowCreateModal(true)}
                                    className="empty-action-btn"
                                >
                                    <Plus size={18} />
                                    Crear primer cliente
                                </Button>
                            )}
                        </div>
                    )}

                    {!loading && filteredClientes.length > 0 && (
                        <table className="customers-table">
                            <thead>
                                <tr>
                                    <th>Cliente</th>
                                    <th>Precio Venta</th>
                                    <th>Crédito</th>
                                    <th>Teléfonos</th>
                                    <th>Direcciones</th>
                                    <th>Registro</th>
                                    <th data-type="actions">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredClientes.map((cliente) => (
                                    <tr key={cliente.id}>
                                        <td>
                                            <div className="customer-info">
                                                <div className="customer-name">
                                                    {cliente.nombre}
                                                </div>
                                                {cliente.nombre_comercial && (
                                                    <div className="customer-commercial">
                                                        {cliente.nombre_comercial}
                                                    </div>
                                                )}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="price-badge">
                                                {cliente.precio_venta === 'precio_lista' && (
                                                    <span className="badge precio-lista">P. Lista</span>
                                                )}
                                                {cliente.precio_venta === 'precio1' && (
                                                    <span className="badge precio-1">Precio 1</span>
                                                )}
                                                {cliente.precio_venta === 'precio2' && (
                                                    <span className="badge precio-2">Precio 2</span>
                                                )}
                                                {cliente.precio_venta === 'precio3' && (
                                                    <span className="badge precio-3">Precio 3</span>
                                                )}
                                                {cliente.precio_venta === 'precio4' && (
                                                    <span className="badge precio-4">Precio 4</span>
                                                )}
                                                {cliente.precio_venta === 'precio5' && (
                                                    <span className="badge precio-5">Precio 5</span>
                                                )}
                                                {cliente.precio_venta === 'precio6' && (
                                                    <span className="badge precio-6">Precio 6</span>
                                                )}
                                                {!cliente.precio_venta && (
                                                    <span className="badge precio-lista">P. Lista</span>
                                                )}
                                            </div>
                                        </td>

                                        <td>
                                            {cliente.tiene_credito ? (
                                                <div className="credit-info">
                                                    <div className="credit-status enabled">

                                                        Crédito activo
                                                    </div>
                                                    <div className="credit-details">
                                                        <span className="credit-amount">${parseFloat(cliente.monto_credito_maximo || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                        <span className="credit-days">{cliente.dias_credito || 0} días</span>
                                                    </div>
                                                    {cliente.saldo_credito_usado > 0 && (
                                                        <div className="credit-used">
                                                            Usado: ${parseFloat(cliente.saldo_credito_usado || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="credit-status disabled">Sin crédito</span>
                                            )}
                                        </td>

                                        <td>
                                            {cliente.telefonos && cliente.telefonos.length > 0 ? (
                                                <div className="phones-list">
                                                    {cliente.telefonos.slice(0, 2).map((tel, index) => (
                                                        <div key={index} className="phone-item">
                                                            <Phone size={14} />
                                                            <span className="phone-number">{tel.telefono}</span>
                                                            <span className="phone-type">({tel.tipo})</span>
                                                        </div>
                                                    ))}
                                                    {cliente.telefonos.length > 2 && (
                                                        <div className="more-items">
                                                            +{cliente.telefonos.length - 2} más
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="no-data">Sin teléfonos</span>
                                            )}
                                        </td>

                                        <td>
                                            {cliente.direcciones && cliente.direcciones.length > 0 ? (
                                                <div className="addresses-list">
                                                    {cliente.direcciones.slice(0, 1).map((dir, index) => (
                                                        <div key={index} className="address-item">
                                                            <MapPin size={14} />
                                                            <span className="address-text">
                                                                {dir.direccion.length > 50
                                                                    ? `${dir.direccion.substring(0, 50)}...`
                                                                    : dir.direccion
                                                                }
                                                            </span>
                                                            {dir.es_principal && (
                                                                <span className="address-principal">★</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {cliente.direcciones.length > 1 && (
                                                        <div className="more-items">
                                                            +{cliente.direcciones.length - 1} más
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="no-data">Sin direcciones</span>
                                            )}
                                        </td>

                                        <td className="date">
                                            {formatDate(cliente.fecha_creacion)}
                                        </td>

                                        <td className="actions">
                                            <div className="actions-compact">
                                                <button
                                                    className="action-btn-compact"
                                                    onClick={() => {
                                                        setSelectedCliente(cliente);
                                                        setShowEditModal(true);
                                                    }}
                                                    title="Editar"
                                                >
                                                    <Edit size={14} />
                                                </button>

                                                <button
                                                    className="action-btn-compact delete"
                                                    onClick={() => handleDeleteCliente(cliente.id, cliente.nombre, cliente.apellido)}
                                                    title="Desactivar"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Modales */}
                {showCreateModal && (
                    <Modal
                        title="Crear Nuevo Cliente"
                        onClose={() => setShowCreateModal(false)}
                        size="xlarge"
                    >
                        <CustomerForm
                            onSubmit={handleCreateCliente}
                            onCancel={() => setShowCreateModal(false)}
                            isLoading={loading}
                        />
                    </Modal>
                )}

                {showEditModal && selectedCliente && (
                    <Modal
                        title="Editar Cliente"
                        onClose={() => {
                            setShowEditModal(false);
                            setSelectedCliente(null);
                        }}
                        size="xlarge"
                    >
                        <CustomerForm
                            cliente={selectedCliente}
                            onSubmit={handleEditCliente}
                            onCancel={() => {
                                setShowEditModal(false);
                                setSelectedCliente(null);
                            }}
                            isLoading={loading}
                            isEdit={true}
                        />
                    </Modal>
                )}

            </div>
        </GoogleMapsLoader>
    );
};

export default CustomerManagementPage;